import React from "react";
import { motion } from "framer-motion";
import {
  FaHeart,
  FaLeaf,
  FaStar,
  FaTruck,
  FaAward,
  FaUsers,
} from "react-icons/fa";
import {
  DecorativeElement,
  FloatingElement,
} from "../../components/Decorative";
import { TOAST_TAB_BASE_URL } from "../../utils/toastTabLinks";
import biriyani from "../../img/biriyani.webp";

const About = () => {
  const values = [
    {
      icon: <FaLeaf className="text-3xl" />,
      title: "Fresh Ingredients",
      description:
        "We source the finest ingredients daily for authentic flavors",
      color: "from-[#227a32] to-[#1a5f27]",
    },
    {
      icon: <FaHeart className="text-3xl" />,
      title: "Made with Love",
      description: "Every dish is prepared with passion and dedication",
      color: "from-[#ac9430] to-[#947d25]",
    },
    {
      icon: <FaStar className="text-3xl" />,
      title: "Excellence",
      description: "Committed to delivering the highest quality food",
      color: "from-[#ac9430] to-[#227a32]",
    },
    {
      icon: <FaTruck className="text-3xl" />,
      title: "Fast Delivery",
      description: "Hot meals delivered fresh within 30-45 minutes",
      color: "from-[#227a32] to-[#ac9430]",
    },
  ];

  const stats = [
    { number: "10k+", label: "Happy Customers" },
    { number: "50+", label: "Menu Items" },
    { number: "20+", label: "Years Experience" },
    { number: "5★", label: "Average Rating" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#1a1812] via-[#2a2620] to-[#1a1812] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1920&h=600&fit=crop"
            alt="Indian cuisine"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-[#ac9430]/20 px-4 py-2 rounded-full mb-6"
          >
            <FaHeart className="text-[#ac9430]" />
            <span className="text-sm font-semibold text-[#ac9430]">
              Our Story
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            About Royal Curry Leaf
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-xl mb-8 max-w-3xl mx-auto text-gray-300"
          >
            Bringing authentic Indian flavors to your home since 2004
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <FloatingElement delay={0}>
              <div className="relative">
                <img
                  src={biriyani}
                  alt="Hyderabadi Biryani"
                  className="rounded-2xl shadow-2xl w-full"
                />
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-[#947d25] to-[#ac9430] text-white p-6 rounded-2xl shadow-xl">
                  <div className="text-4xl font-bold">20+</div>
                  <div className="text-sm">Years of Excellence</div>
                </div>
              </div>
            </FloatingElement>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="inline-flex items-center gap-2 bg-[#ac9430]/10 px-4 py-2 rounded-full mb-4">
                <span className="text-sm font-semibold text-[#ac9430]">
                  Our Journey
                </span>
              </div>
              <h2 className="text-4xl font-bold text-headingColor">
                A Legacy of Flavor
              </h2>
              <p className="text-lg text-textColor leading-relaxed">
                Founded in 2004, Royal Curry Leaf began as a dream to share
                authentic Indian cuisine with food lovers everywhere. What
                started as a small kitchen has grown into a beloved ghost
                kitchen serving thousands of happy customers.
              </p>
              <p className="text-lg text-textColor leading-relaxed">
                Our chefs bring decades of experience and traditional recipes
                passed down through generations. We believe in using only the
                freshest ingredients and authentic spices to create dishes that
                transport you to the streets of India.
              </p>
              <div className="flex gap-4">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  onClick={() => window.open(TOAST_TAB_BASE_URL, '_blank')}
                  className="bg-gradient-to-br from-[#947d25] to-[#ac9430] hover:from-[#ac9430] hover:to-[#c4ab3d] text-white px-6 py-3 rounded-full font-semibold shadow-lg cursor-pointer transition-all duration-300"
                >
                  Order Now
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="border-2 border-[#ac9430] text-[#ac9430] hover:bg-[#ac9430] hover:text-white px-6 py-3 rounded-full font-semibold cursor-pointer transition-all duration-300"
                >
                  View Menu
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#947d25] to-[#ac9430] mb-2">
                  {stat.number}
                </div>
                <div className="text-textColor font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 relative">
        <DecorativeElement type="corners" />
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-[#ac9430]/10 px-4 py-2 rounded-full mb-4"
            >
              <span className="text-sm font-semibold text-[#ac9430]">
                Our Values
              </span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-headingColor mb-4">
              What We Stand For
            </h2>
            <p className="text-lg text-textColor max-w-2xl mx-auto">
              Core principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500"
                whileHover={{ y: -10 }}
              >
                <motion.div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${value.color} text-white mb-6 shadow-lg`}
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                >
                  {value.icon}
                </motion.div>

                <h3 className="text-xl font-bold text-headingColor mb-3 group-hover:text-[#ac9430] transition-colors">
                  {value.title}
                </h3>
                <p className="text-textColor leading-relaxed">
                  {value.description}
                </p>

                <motion.div
                  className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${value.color}`}
                  initial={{ width: 0 }}
                  whileInView={{ width: "100%" }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 0.6 }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1812] via-[#2a2620] to-[#1a1812] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-6"
          >
            <FaAward className="text-6xl text-[#ac9430] mx-auto mb-4" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Experience Authentic Indian Cuisine
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg mb-8 text-gray-300"
          >
            Order online now and taste the difference that passion and quality
            make
          </motion.p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-r from-[#ac9430] to-[#227a32] text-white px-8 py-3 rounded-full font-semibold shadow-lg"
            >
              Order Now
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              className="bg-white text-gray-900 px-8 py-3 rounded-full font-semibold"
            >
              View Menu
            </motion.button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
